/**
 * jQuery plugin to animate on scroll... and also for lazy images
 * created by bogdan.b@codingheads.com
 * requires IntersectionObserver
 */

// deep compare objects
Object.deepEquals = function(x, y) {
  if (x === y) return true;
  // if both x and y are null or undefined and exactly the same

  if (!(x instanceof Object) || !(y instanceof Object)) return false;
  // if they are not strictly equal, they both need to be Objects

  if (x.constructor !== y.constructor) return false;
  // they must have the exact same prototype chain, the closest we can do is
  // test there constructor.

  for (var p in x) {
    if (!x.hasOwnProperty(p)) continue;
    // other properties were tested using x.constructor === y.constructor

    if (!y.hasOwnProperty(p)) return false;
    // allows to compare x[ p ] and y[ p ] when set to undefined

    if (x[p] === y[p]) continue;
    // if they have the same strict value or identity then they are equal

    if (typeof x[p] !== 'object') return false;
    // Numbers, Strings, Functions, Booleans must be strictly equal

    if (!Object.deepEquals(x[p], y[p])) return false;
    // Objects and Arrays must be tested recursively
  }

  for (p in y) {
    if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
    // allows x[ p ] to be set to undefined
  }
  return true;
};

window.throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

jQuery(function($) {
  // store the observers into an array, so that we might reuse them
  const savedObservers = [];

  // handle the event when the element gets into the viewport
  $.fn.triggerAnimateOnScroll = function({
    animatedClass = 'animation-started',
    callback = false,
    fadeIn = false,
  } = {}) {
    if (this.length) {
      // set the width and height of the image even if it's not loaded yet, so no moving around will happen on load
      this.each((i, target) => {
        // do this in an animation frame
        window.requestAnimationFrame(() => {
          let intoViewTimestamp;

          // custom animated class
          const elementAnimatedClass = target.dataset.animated;
          if (elementAnimatedClass) {
            animatedClass = elementAnimatedClass;
          }

          // fade in?
          if ('fadeIn' in target.dataset) {
            fadeIn = true;
          }

          // add animation class
          const lazyAnimClass = target.dataset.lazyAnimation;
          if (animatedClass && animatedClass.length && !lazyAnimClass) {
            target.classList.add(animatedClass);
          }

          // trigger the callback
          if (callback) {
            window.requestAnimationFrame(() =>
              callback(target, {
                animatedClass,
                callback,
                fadeIn,
                lazyAnimClass,
              })
            );
          }

          // if this is a lazy-image animation, do things differently
          if (lazyAnimClass) {
            target.classList.add('opacity-0');
            setTimeout(() => {
              target.classList.add(lazyAnimClass);
              target.classList.remove('opacity-0'); // prevent fading out first
            }, 50);
          }

          // trigger animation on imagesLoaded event
          const dataset = target.dataset,
            interchange = dataset.lazyInterchange,
            isLazyBg = 'lazyBg' in dataset,
            isLazyImg = 'lazyImg' in dataset,
            interchangeSizes = dataset.interchangeSizes;

          if (interchange || isLazyBg || isLazyImg) {
            imagesLoaded(
              target,
              {
                background: true,
              },
              () => {
                target.classList.add(animatedClass);
                target.dataset.lazyLoaded = true;
                if (fadeIn) {
                  // wait for a minimum interval before fading in
                  const minInterval = 200,
                    time = new Date(),
                    interval = time.getTime() - intoViewTimestamp;
                  if (target.tagName == 'IMG' || interval > minInterval) {
                    target.style.opacity = 1;
                  } else {
                    setTimeout(() => {
                      target.style.opacity = 1;
                    }, minInterval - interval);
                  }
                }
                if (isLazyImg && !('interchangeSizes' in target.dataset)) {
                  // trigger a window resize - but throttle it
                  throttle(() => {
                    if ('requestIdleCallback' in window) {
                      window.requestIdleCallback(() => {
                        $(window).trigger('resize');
                      });
                    } else {
                      window.requestAnimationFrame(() => {
                        $(window).trigger('resize');
                      });
                    }
                  }, 100);
                }
                if (
                  interchangeSizes &&
                  (isLazyImg ||
                    (interchange && 'foundationLoaded' in target.dataset))
                ) {
                  if (isLazyImg) target.dataset.imgLoaded = true;
                  target.removeAttribute('width');
                  target.removeAttribute('height');
                  target.style.height = '';
                  target.style.width = '';
                }
              }
            );
            if (fadeIn) {
              target.style.opacity = 0;
              target.style.transition = 'opacity 0.3s';
              const time = new Date();
              intoViewTimestamp = time.getTime();
            }
            // handle lazy interchange cases
            if (interchange) {
              target.dataset.interchange = interchange;
              target.removeAttribute('data-lazy-interchange');
              target.dataset.foundationLoaded = true;
              $(target).foundation();
            }
            if (isLazyBg || isLazyImg) {
              const url = dataset.src;
              if (isLazyBg) {
                // background image
                target.style.backgroundImage = `url(${url})`;
                target.removeAttribute('data-lazy-bg');
              } else {
                // img element
                if (url) {
                  target.setAttribute('src', url);
                  target.removeAttribute('data-src');
                }
                target.removeAttribute('data-lazy-img');
                const srcset = dataset.srcset,
                  sizes = dataset.sizes;
                if (srcset) {
                  target.setAttribute('srcset', srcset);
                  target.removeAttribute('data-srcset');
                }
                if (sizes) {
                  target.setAttribute('sizes', sizes);
                  target.removeAttribute('data-sizes');
                }
              }
            }
          }

          // trigger an event...
          target.dispatchEvent(
            new CustomEvent('animatedOnScroll', {
              bubbles: true,
            })
          );
        });
      });
    }
    return this;
  };

  // animate on scroll, using the intersection observer
  $.fn.animateOnScroll = function({
    animatedClass = 'animation-started',
    initAttribute = 'animOnScrollInit',
    options = {
      rootMargin: '100px 0px', // trigger the animation before the objects are in sight
    },
    callback = false,
    removable = true,
    fadeIn = false,
    animateWithParent = true,
  } = {}) {
    const args = {
      animatedClass,
      options,
      callback,
      removable,
      fadeIn,
      animateWithParent,
    };

    if (this.length) {
      this.each((i, target) => {
        // don't duplicate activations
        if (initAttribute in target.dataset) return;

        // signal that this is initialized
        target.dataset[initAttribute] = true;

        // allow external trigger
        const $target = $(target);
        $target.on('animateOnScroll', function(e) {
          if (!animateWithParent || e.target == target) {
            $target.triggerAnimateOnScroll(args);
          }
        });

        // set the width and height of the image even if it's not loaded yet, so no moving around will happen on load
        const interchangeSizesJSON = target.dataset.interchangeSizes;
        if (
          interchangeSizesJSON &&
          typeof Foundation.MediaQuery != 'undefined'
        ) {
          let interchangeSizes;
          try {
            interchangeSizes = JSON.parse(interchangeSizesJSON);
          } catch (error) {
            interchangeSizes = [];
          }
          if (interchangeSizes) {
            const addImageSizes = () => {
              window.requestAnimationFrame(() => {
                let currentSize = Foundation.MediaQuery.current;
                // if foundation is already loaded, don't do this any more
                if ('foundationLoaded' in target.dataset) {
                  ['changed.zf.mediaquery'].forEach(eventType => {
                    window.removeEventListener(eventType, addImageSizes);
                  });
                  return;
                }
                if (!(currentSize in interchangeSizes)) {
                  // search a valid image size
                  const availableSizes = Object.getOwnPropertyNames(
                    interchangeSizes
                  ).reverse();
                  for (let i = 0, max = availableSizes.length; i < max; i++) {
                    if (Foundation.MediaQuery.atLeast(availableSizes[i])) {
                      currentSize = availableSizes[i];
                      break;
                    }
                  }
                }
                if (currentSize in interchangeSizes) {
                  const width = target.clientWidth,
                    minWidth = Math.min(width, interchangeSizes[currentSize].w),
                    height =
                      (minWidth / interchangeSizes[currentSize].w) *
                      interchangeSizes[currentSize].h;
                  target.setAttribute('width', interchangeSizes[currentSize].w);
                  target.setAttribute(
                    'height',
                    interchangeSizes[currentSize].h
                  );
                  target.style.width = minWidth + 'px';
                  target.style.height = height + 'px';
                }
              });
            };
            addImageSizes();
            ['changed.zf.mediaquery'].forEach(eventType => {
              window.addEventListener(eventType, addImageSizes);
            });
          }
        }
      });

      // search for an existing observer
      let observer = false;
      if (savedObservers.length) {
        const match = savedObservers.filter(savedObserver =>
          Object.deepEquals(savedObserver.args, args)
        );
        if (match && match.length) {
          observer = match[0].observer;
        }
      }

      // no previous matching observer found. Create a new one
      if (!observer) {
        observer = new IntersectionObserver(function(entries) {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // don't reobserve
              if (removable) observer.unobserve(entry.target);

              entry.target.dispatchEvent(
                new CustomEvent('animateOnScroll', {
                  bubbles: true,
                  detail: {
                    entry,
                    observer,
                    ...args,
                  },
                })
              );
            }
          });
        }, options);
        // save the observer
        savedObservers.push({
          observer,
          args,
        });
      }

      // initialize the observer on this element
      this.each(function() {
        observer.observe(this);
      });

      this.on('animatedOnScroll', function() {
        if (removable) observer.unobserve(this);
      });
    }
    return this;
  };
});
